import React, { useEffect } from "react";
import ApiClient from "../../../services/ApiClient";
import _ from "lodash";
import { useSnackbar } from "notistack";
import { getCaseLink } from "../../../services/Product/ProductService/productService";
import { getCasePdfFormParams } from "./casePdfGeneratorService";
import { clearSessionStorageState, loadSessionStorageState } from "../../../services/browserStorageService";
import SubmitButton from "../../Button/SubmitButton";
import casePdfGeneratorStyle from "./casePdfGeneratorStyle";
import PdfGeneratorFormElements from "./PdfGeneratorFormElements";
import PdfGeneratorPreview from "./PdfGeneratorPreview";
import ContentBox from "../../ContentBox/ContentBox";
import { Box, MenuItem, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { fetchCollection, queryKeys } from "../../../services/ReactQuery/reactQueryService";
import { useCurrentUser } from "../../../provider/CurrentUserProvider";
import { getMediaObjectFilters } from "../../../services/mediaObjectService";
import { FormContainer, useForm } from "react-hook-form-mui";
import { notNoneSelectValidatorDefaultFieldBased } from "../../../services/reactHookFormValidationRules";
import SelectElement from "../../ReactHookFormElements/SelectElement";
import { scrollToAnchor } from "../../../theme/commonStyles";
import { HydraCollectionResult } from "../../../types/HydraCollectionResult";
import { MediaObject } from "../../../types/MediaObject";
import PdfGeneratorDraftList from "./PdfGeneratorDraftList";
import PdfGeneratorDraftSave from "./PdfGeneratorDraftSave";
import { CasePdfGeneratorFormValues } from "../../../types/CasePdfGenerator";
import { useCurrentEditor } from "@tiptap/react";
import AddTextBlock from "../../LbEditor/Toolbar/AddTextBlock";
import AiWizard from "../../LbEditor/Toolbar/AiWizard/AiWizard";
import { defaultScopes } from "../../LbEditor/Toolbar/AiWizard/AiWizardModal";
import { ProductUnion } from "../../../types/ProductUnion";
import dayjs from "dayjs";

type CasePdfGeneratorProps = {
  product: ProductUnion;
  documentTypeLabel?: string;
  ctaLabel?: string;
};

const CasePdfGenerator = ({
  product,
  documentTypeLabel = "Individuelles",
  ctaLabel = "Schreiben generieren",
}: CasePdfGeneratorProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const currentUser = useCurrentUser();
  const { editor } = useCurrentEditor();

  const mediaObjectsFilter = getMediaObjectFilters(product, currentUser);

  const { data: mediaObjects }: { data: HydraCollectionResult<MediaObject> | undefined } = useQuery({
    queryKey: queryKeys.collection("media_objects", mediaObjectsFilter),
    queryFn: () => fetchCollection("media_objects", mediaObjectsFilter),
    enabled: !!product,
  });

  const onSubmit = async () => {
    await ApiClient.post("files/generate/" + product.productClassName + "/" + product.id, {
      body: JSON.stringify({
        ...getCasePdfFormParams({ values: formContext.getValues(), editor, product, currentUser }),
        documentIdentifier: "customPdf",
      }),
    });

    enqueueSnackbar(
      currentUser.isExternal
        ? "Schriftsatz wurde erfolgreich erstellt und am Fall angehängt"
        : "Schreiben wurde erfolgreich erstellt",
      {
        variant: "custom",
        buttonType: "link",
        buttonLink: getCaseLink(product, currentUser.isExternal ? "/extern" : ""),
        buttonTarget: "_self",
        buttonText: "Zum Fall",
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "center",
        },
        autoHideDuration: 10000,
      },
    );

    formContext.reset(initialValues);
    editor?.commands.clearContent();
    clearSessionStorageState("customPdfHtml" + product.id);
    clearSessionStorageState("customPdfValues" + product.id);
  };

  const initialValues = {
    documentTemplate: "customPdf",
    recipientType: "custom",
    address: {
      name: "",
      department: "",
      subDepartment: "",
      streetAddress: "",
      postalCode: "",
      addressLocality: "",
    },
    senderMedium: "none",
    externalReference: "",
    externalReferenceSelect: "free",
    filename: dayjs().format("YYYYMMDD") + "_Schreiben",
    description: "",
    showInGallery: "true",
    sendEmailToCustomer: "false",
    selectedMediaObjects: [],
    electronicFileFolderPath: "none",
    caseReferenceNumber: "",
    insurancePolicyNumber: "",
  };

  useEffect(() => {
    const sessionStorageValues = loadSessionStorageState("customPdfValues" + product.id);
    const sessionStorageHtml = loadSessionStorageState("customPdfHtml" + product.id);
    if (sessionStorageHtml) {
      editor?.commands.setContent(sessionStorageHtml);
    }
    if (sessionStorageValues) {
      formContext.reset({
        address: {
          name: sessionStorageValues.address.name,
          department: sessionStorageValues.address.department,
          subDepartment: sessionStorageValues.address.subDepartment,
          streetAddress: sessionStorageValues.address.streetAddress,
          postalCode: sessionStorageValues.address.postalCode,
          addressLocality: sessionStorageValues.address.addressLocality,
        },
        senderMedium: sessionStorageValues.senderMedium,
        externalReference: sessionStorageValues.externalReference,
        externalReferenceSelect: sessionStorageValues.externalReferenceSelect,
        filename: sessionStorageValues.filename,
        description: sessionStorageValues.description,
        showInGallery: sessionStorageValues.showInGallery,
        sendEmailToCustomer: sessionStorageValues.sendEmailToCustomer,
        selectedMediaObjects: sessionStorageValues.selectedMediaObjects,
        electronicFileFolderPath: sessionStorageValues.electronicFileFolderPath,
        caseReferenceNumber: sessionStorageValues.caseReferenceNumber,
        insurancePolicyNumber: sessionStorageValues.insurancePolicyNumber,
      });
    }
  }, []);

  const formContext = useForm<CasePdfGeneratorFormValues>({
    mode: "onBlur",
    defaultValues: initialValues,
  });

  let additionalToolbarActions = [<AddTextBlock context={"legalDocuments"} />];

  let aiWizardRecipientType = formContext.watch("recipientType");
  const [fullMatch, accessor, id] = aiWizardRecipientType?.match(/^(.+)[[.](\d+)/) ?? [];

  switch (accessor) {
    case "processParticipantsPeople":
      aiWizardRecipientType = product.processParticipantsPeople.find(
        (participant: { id: number }) => String(participant.id) === id,
      )?.labelType;
      break;
    case "processParticipantsOrganizations":
      aiWizardRecipientType = product.processParticipantsOrganizations[id].organization.labelType;
      break;
  }

  if (
    [
      "death",
      "alimony",
      "familyLawCase",
      "generalCivilLaw",
      "employmentContract",
      "civilServiceLaw",
      "rentalContract",
      "divorce",
      "trafficAccident",
      "settlement",
      "traffic",
    ].includes(product.productClassName) ||
    product.productCategory === "contractLaw"
  ) {
    additionalToolbarActions.push(
      <AiWizard
        product={product}
        componentScopes={
          formContext.watch("documentTemplate", "customPdf") === "extrajudicialLetter"
            ? {
                customExtrajudicialLetter: defaultScopes.customExtrajudicialLetter,
              }
            : defaultScopes
        }
        sx={{ position: "absolute", top: 5, right: 0 }}
        defaultScopeKey={
          formContext.watch("documentTemplate", "customPdf") === "extrajudicialLetter"
            ? "customExtrajudicialLetter"
            : "customPdf"
        }
        promptContext={{
          recipientType: aiWizardRecipientType,
        }}
      />,
    );
  }

  return (
    <>
      <Box sx={scrollToAnchor} id={"casePdfGenerator"} />
      <ContentBox
        headline={!currentUser.isExternal ? "Schreiben erstellen" : documentTypeLabel + " Schreiben erstellen"}
      >
        <FormContainer formContext={formContext}>
          <PdfGeneratorDraftList product={product} formContext={formContext} />
          <PdfGeneratorFormElements
            product={product}
            mediaObjects={mediaObjects?.["hydra:member"] || []}
            additionalEditorToolbarActions={additionalToolbarActions}
          />
          <PdfGeneratorPreview
            product={product}
            values={formContext.getValues()}
            getParams={() =>
              getCasePdfFormParams({
                values: formContext.getValues(),
                editor,
                product,
                currentUser,
              })
            }
            mediaObjects={mediaObjects?.["hydra:member"] || []}
            disabled={!product.responsibleLawyer}
          />
          <PdfGeneratorDraftSave product={product} formContext={formContext} />
          {formContext.watch("documentTemplate", "customPdf") === "customPdf" && (
            <Box sx={{ width: 200, margin: "0 auto" }}>
              <SelectElement
                label={"Ablage in Ordner"}
                name={"electronicFileFolderPath"}
                validation={
                  formContext.watch("documentTemplate", "customPdf") === "customPdf"
                    ? { validate: notNoneSelectValidatorDefaultFieldBased }
                    : {}
                }
              >
                <MenuItem value={"none"} disabled>
                  {" "}
                </MenuItem>
                <MenuItem value={"Außergerichtliches Verfahren"}>Außergerichtliches Verfahren</MenuItem>
                <MenuItem value={"Gerichtliches Verfahren"}>Gerichtliches Verfahren</MenuItem>
                <MenuItem value={"Mandantschaft"}>Mandantschaft</MenuItem>
                <MenuItem value={"RSV"}>RSV</MenuItem>
                <MenuItem value={"ToDo"}>ToDo</MenuItem>
                <MenuItem value={"Sonstiges"}>Sonstiges</MenuItem>
              </SelectElement>
            </Box>
          )}

          <SubmitButton
            sx={casePdfGeneratorStyle.submitButton}
            error={_.keys(formContext.formState.errors).length > 0}
            fullWidth={false}
            disabled={(editor ? editor.isEmpty : true) || !product.responsibleLawyer} //formContext.watch("editor") ? formContext.watch("editor").isEmpty : true
            isLoading={formContext.formState.isSubmitting}
            variant={"contained"}
            onClick={formContext.handleSubmit(onSubmit)}
          >
            {ctaLabel}
          </SubmitButton>

          {!product.responsibleLawyer && (
            <Typography
              align={"center"}
              sx={{
                color: "error.main",
                marginTop: "1rem",
              }}
            >
              Kein Anwalt am Fall vorhanden. Es können keine Schriftsätze erstellt werden.
            </Typography>
          )}
        </FormContainer>
      </ContentBox>
    </>
  );
};

export default CasePdfGenerator;
